import { useMemo } from 'react';

import { OrganizationTypes, Roles } from '@/common/constants';
import { useAuthStore } from '@/modules/user/stores';

export interface AccessRequirement {
  permission?: string;
  organizationType?: OrganizationTypes;
  isException?: boolean;
  role?: Roles;
}

const useHasAccess = (accessRequirements?: AccessRequirement[]): boolean => {
  const { user } = useAuthStore();
  return useMemo(() => {
    if (!accessRequirements?.length) {
      return true;
    }

    const hasException = accessRequirements
      .filter((accessRequirement) => accessRequirement.isException)
      .some((exception) => {
        const hasExceptionByPermission = exception?.permission
          && (user?.permissions || [])
            .some((permission) => permission === exception?.permission);
        const hasExceptionByOrganizationType = exception?.organizationType
          && user?.organizationType
          && exception?.organizationType === user?.organizationType;
        const hasExceptionByRole = exception?.role
          && user?.role
          && exception?.role === user?.role;

        return hasExceptionByPermission || hasExceptionByOrganizationType || hasExceptionByRole;
      });
    if (hasException) {
      return false;
    }

    const accessRequirementsWithoutExceptions = accessRequirements
      .filter((accessRequirement) => !accessRequirement.isException);
    return accessRequirementsWithoutExceptions.length === 0
      || accessRequirementsWithoutExceptions
        .filter((accessRequirement) => !accessRequirement.isException)
        .some((accessRequirement) => {
          const isAccessibleByPermissions = !accessRequirement.permission
            || (user?.permissions || [])
              .some((permission) => permission === accessRequirement.permission);
          const isAccessibleByOrganizationType = !accessRequirement.organizationType
            || user?.organizationType === accessRequirement.organizationType;
          const isAccessibleByRole = !accessRequirement.role
            || user?.role === accessRequirement.role;
          return isAccessibleByPermissions && isAccessibleByOrganizationType && isAccessibleByRole;
        });
  }, [accessRequirements, user]);
};

export default useHasAccess;
